import { inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CanActivateChildFn, CanActivateFn, Router } from '@angular/router';
import { map } from 'rxjs';
import { PermissionCode, PermissionService } from './permission.service';

/**
 * Lets somebody into a route if they hold ANY of the permissions named:
 *
 *     { path: 'hr', canActivate: [requirePermission(P.hrView)],
 *       canActivateChild: [requirePermission(P.hrView)], loadChildren: ... }
 *
 * Use the same codes the item has in the sidebar (`navigation-access.ts`), or somebody gets a menu
 * item that bounces them, or a page with no way to reach it.
 *
 * Like everything on this side, a courtesy: it stops somebody landing on a page that would only
 * fill with refusals. The endpoints are the boundary.
 */
export function requirePermission(...codes: PermissionCode[]): CanActivateFn & CanActivateChildFn {
    return () => {
        const permissions = inject(PermissionService);
        const router = inject(Router);
        const snack = inject(MatSnackBar);

        return permissions.load().pipe(
            map(() => {
                if (permissions.hasAny(...codes)) return true;

                snack.open('You do not have access to that page.', 'OK', { duration: 4000 });

                // Never bounce to sign-in: they ARE signed in, and that loop is how people get stuck.
                return router.parseUrl('/dashboard');
            })
        );
    };
}
